import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Gift, Heart, Users, Sparkles } from 'lucide-react';
import { getPageContent } from '../services/api';

const PotraitChocolateLanding = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const data = await getPageContent();
        setContent(data);
      } catch (error) {
        console.error('Error loading page content:', error);
        // Use default content if API fails
        setContent(getDefaultContent());
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, []);

  const getDefaultContent = () => ({
    hero: {
      background_image: 'https://images.unsplash.com/photo-1623053045271-e53d909ef33b?w=1600',
      video_url: 'https://customer-assets.emergentagent.com/job_designmycard-3/artifacts/u0eyy2yi_2025-11-03-092714884.mp4',
      heading: 'Portrait Chocolate',
      subheading: 'Artisan Photo Printed Chocolates',
      description: 'We transform your cherished photos into edible masterpieces, adding a touch of sweetness and sentimentality to every occasion.',
      tagline: 'Celebrate Life\'s Sweet Moments with Premium Chocolate'
    },
    service_card_1: {
      title: 'Edible Greetings',
      description: 'Send delicious personalized chocolate postcards featuring your favorite photos. Perfect for every occasion!',
      background_image: 'https://images.unsplash.com/photo-1607940471713-a9376f150a0d?w=800',
      features: [],
      button_text: 'Shop Edible Greetings →',
      button_link: '/edible-greetings'
    },
    service_card_2: {
      title: 'Live Photo Chocolates',
      description: 'State-of-the-art printing technology creates stunning images on premium chocolate',
      background_image: 'https://images.unsplash.com/photo-1695649912699-435a5bc20203?w=800',
      features: [],
      button_text: 'Contact Us for Custom Orders',
      button_link: '/contact'
    },
    services_section: {
      heading: 'Our Special Services',
      description: 'We specialize in creating unique and personalized live photo printed chocolates that capture the essence of your special events. We transform your cherished photos into edible masterpieces, adding a touch of sweetness and sentimentality to every occasion.'
    },
    cta_section: {
      heading: "Let's Create Something Sweet and Memorable",
      description: 'Contact us today and we can transform your photos into unforgettable chocolate creations.',
      button1_text: 'Shop Now',
      button1_link: '/edible-greetings',
      button2_text: 'Contact Us',
      button2_link: '/contact'
    },
    stats: {
      happy_customers: '5000+',
      average_rating: '4.9★',
      chocolates_created: '15000+',
      satisfaction: '100%'
    },
    testimonial_videos: [
      {
        video_url: 'https://customer-assets.emergentagent.com/job_design-eat-send/artifacts/mucw44rb_AQOnhoynuyB4pTVdZRsepwbaJGuUimYjftp8og_vuicTSMFEepu62M6W3SmJwV6JReJccb4Qz6JW1D77tNmWvNiw9nhtwqekJ16UiJqpOgCdKA%20%281%29.mp4',
        rating: 5,
        text: 'The photo quality on the chocolates was absolutely stunning! Our wedding guests were amazed. It\'s such a unique and memorable way to celebrate special moments.',
        customer_name: 'Sarah M.',
        customer_title: 'Verified Customer'
      },
      {
        video_url: 'https://customer-assets.emergentagent.com/job_design-eat-send/artifacts/9tddx11b_AQOMoiBSNBlINNrYv0VgjiBfLfZ_AqDlou7miC4d_Aw2h5-zN7eOK6nQQ5wePuu5TVk8m4yXULrJ8NjuNYBvNYsdBdo0wN4nLLmNuLrNLNjF6qGexw.mp4',
        rating: 5,
        text: 'Best gift ever! The premium chocolate quality is incredible and seeing our photos printed on chocolate was magical. Highly recommend!',
        customer_name: 'Michael T.',
        customer_title: 'Verified Customer'
      }
    ]
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero Section with Video */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url(${content.hero.background_image})`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/40 via-purple-900/30 to-pink-900/40"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-12">
            <h1 className="elegant-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 animate-fade-in tracking-wide">
              {content.hero.heading}
            </h1>
            <p className="elegant-subheading text-2xl sm:text-3xl text-gray-800 mb-8 max-w-3xl mx-auto font-semibold">
              {content.hero.subheading}
            </p>
            <p className="text-lg sm:text-xl text-gray-700 mb-4 max-w-2xl mx-auto leading-relaxed">
              {content.hero.description}
            </p>
            <p className="elegant-subheading text-base sm:text-lg text-gray-600 mb-8 italic">
              {content.hero.tagline}
            </p>
          </div>

          {/* Featured Video */}
          <div className="max-w-md mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white aspect-[9/16]">
              <video 
                className="w-full h-full object-cover"
                controls
                poster="https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=400&h=711&fit=crop&q=80"
                preload="metadata"
              >
                <source src={content.hero.video_url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center text-gray-600 mt-4 text-sm">
              Watch how we create stunning photo chocolates
            </p>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <h2 className="elegant-heading text-4xl sm:text-5xl font-bold text-gray-900 mb-6 tracking-wide">
            {content.services_section?.heading || 'Our Special Services'}
          </h2>
          <p className="elegant-subheading text-xl sm:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            {content.services_section?.description || 'We specialize in creating unique and personalized live photo printed chocolates that capture the essence of your special events.'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-stretch mb-20">
          {/* Service Card 1 - Edible Greetings */}
          <Card className="relative overflow-hidden p-8 border-2 border-purple-200 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-30"
              style={{
                backgroundImage: `url(${content.service_card_1.background_image})`,
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50/95 to-pink-50/95"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-4">
                <div className="bg-purple-100 p-4 rounded-full shadow-lg">
                  <Gift className="w-12 h-12 text-purple-600" />
                </div>
              </div>
              <h2 className="elegant-heading text-2xl font-bold text-gray-900 mb-3 text-center">
                {content.service_card_1.title}
              </h2>
              <p className="text-gray-700 text-center mb-4 text-base leading-relaxed">
                {content.service_card_1.description}
              </p>
              <div className="space-y-2 mb-6 text-sm">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700">Photo printing on chocolate</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700">High-resolution images</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700">Custom designs</span>
                </div>
              </div>
              <Link to={content.service_card_1.button_link}>
                <Button className="w-full py-4 text-base bg-purple-600 hover:bg-purple-700 shadow-lg">
                  {content.service_card_1.button_text}
                </Button>
              </Link>
            </div>
          </Card>

          {/* Service Card 2 - Live Photo Booth */}
          <Card className="relative overflow-hidden p-8 border-2 border-pink-200 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-25"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800)',
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-br from-pink-50/95 to-rose-50/95"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-4">
                <div className="bg-pink-100 p-4 rounded-full shadow-lg">
                  <span className="text-4xl">📸</span>
                </div>
              </div>
              <h2 className="elegant-heading text-2xl font-bold text-gray-900 mb-3 text-center">
                Live Photo Booth
              </h2>
              <p className="text-gray-700 text-center mb-4 text-base leading-relaxed">
                Take fun photos with props and filters! Perfect for events, parties, and celebrations.
              </p>
              <div className="space-y-2 mb-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-pink-500">🎭</span>
                  <span className="text-gray-700">Fun props & overlays</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-pink-500">🎨</span>
                  <span className="text-gray-700">Beautiful backdrops</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-pink-500">📱</span>
                  <span className="text-gray-700">Download & share instantly</span>
                </div>
              </div>
              <Link to="/live-photobooth">
                <Button className="w-full py-4 text-base bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-lg">
                  Try Photo Booth →
                </Button>
              </Link>
            </div>
          </Card>

          {/* Service Card 3 - Custom Orders */}
          <Card className="relative overflow-hidden p-8 border-2 border-amber-200 hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-25"
              style={{
                backgroundImage: `url(${content.service_card_2.background_image})`,
              }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/95 to-orange-50/95"></div>
            <div className="relative z-10">
              <div className="flex justify-center mb-4">
                <div className="bg-amber-100 p-4 rounded-full shadow-lg">
                  <Sparkles className="w-12 h-12 text-amber-600" />
                </div>
              </div>
              <h2 className="elegant-heading text-2xl font-bold text-gray-900 mb-3 text-center">
                {content.service_card_2.title}
              </h2>
              <p className="text-gray-700 text-center mb-4 text-base leading-relaxed">
                {content.service_card_2.description}
              </p>
              <div className="space-y-2 mb-6 text-sm text-gray-700">
                <p className="text-center font-semibold elegant-subheading">Perfect For:</p>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-amber-400 rounded-full shadow"></div>
                  <span>Birthdays & Engagements</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-amber-400 rounded-full shadow"></div>
                  <span>Weddings & Anniversaries</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-amber-400 rounded-full shadow"></div>
                  <span>Corporate Events</span>
                </div>
              </div>
              <Link to={content.service_card_2.button_link}>
                <Button className="w-full py-4 text-base bg-amber-600 hover:bg-amber-700 shadow-lg">
                  {content.service_card_2.button_text}
                </Button>
              </Link>
            </div>
          </Card>
        </div>


        {/* Occasions Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-12 mb-16 shadow-lg">
          <h3 className="elegant-heading text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-8">
            Celebrate Life's Milestones with Personalized Chocolates
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <h4 className="elegant-subheading font-semibold text-lg text-purple-600 mb-2">🎂 Birthdays</h4>
              <p className="text-gray-700 leading-relaxed">Surprise your loved ones with live photo printed chocolates featuring their favorite memories.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <h4 className="elegant-subheading font-semibold text-lg text-purple-600 mb-2">💍 Engagements</h4>
              <p className="text-gray-700 leading-relaxed">Commemorate your engagement with custom chocolates showcasing your beautiful engagement photos.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <h4 className="elegant-subheading font-semibold text-lg text-purple-600 mb-2">👰 Weddings</h4>
              <p className="text-gray-700 leading-relaxed">Gift your guests with personalized wedding favors featuring a live photo from your special day.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <h4 className="elegant-subheading font-semibold text-lg text-purple-600 mb-2">💕 Anniversaries</h4>
              <p className="text-gray-700 leading-relaxed">Relive cherished moments with live photo printed chocolates that celebrate your love story.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <h4 className="elegant-subheading font-semibold text-lg text-purple-600 mb-2">🏢 Corporate Events</h4>
              <p className="text-gray-700 leading-relaxed">Make a lasting impression with custom-branded chocolates featuring your company logo or event photos.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-shadow">
              <h4 className="elegant-subheading font-semibold text-lg text-purple-600 mb-2">🎉 Special Occasions</h4>
              <p className="text-gray-700 leading-relaxed">Celebrate seasonal moments with our handcrafted chocolates designed to bring joy to every occasion.</p>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section */}
        <div className="relative overflow-hidden rounded-3xl shadow-xl mb-16">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-15"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1621134283184-5e7881013013?w=1200)',
            }}
          ></div>
          <div className="absolute inset-0 bg-white/90"></div>
          <div className="relative p-12">
            <div className="text-center mb-12">
              <h3 className="elegant-heading text-4xl font-bold text-gray-900 mb-4">We Bring Your Photos to Life</h3>
              <p className="elegant-subheading text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                Our state-of-the-art printing technology allows us to create stunning, high-resolution images on premium chocolate. 
                Each chocolate is a work of art, capturing the vibrancy and detail of your photos.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg">
                  <Gift className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="elegant-heading text-xl font-semibold mb-3 text-gray-900">Premium Chocolate</h3>
                <p className="text-gray-700 leading-relaxed">
                  Made with the finest premium chocolate for exceptional taste and quality that melts perfectly
                </p>
              </div>
              <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className="bg-gradient-to-br from-pink-100 to-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg">
                  <Heart className="w-10 h-10 text-pink-600" />
                </div>
                <h3 className="elegant-heading text-xl font-semibold mb-3 text-gray-900">High-Resolution Printing</h3>
                <p className="text-gray-700 leading-relaxed">
                  Stunning, vibrant images that capture every detail with our advanced printing technology
                </p>
              </div>
              <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
                <div className="bg-gradient-to-br from-amber-100 to-orange-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:shadow-lg">
                  <Users className="w-10 h-10 text-amber-600" />
                </div>
                <h3 className="elegant-heading text-xl font-semibold mb-3 text-gray-900">Craftsmanship & Dedication</h3>
                <p className="text-gray-700 leading-relaxed">
                  Each piece tells a unique story of quality and care, handcrafted with love
                </p>
              </div>
            </div>

          {/* Process Steps */}
            <div className="mt-16 pt-12 border-t border-gray-300">
              <h4 className="elegant-heading text-3xl font-bold text-center text-gray-900 mb-10">How It Works</h4>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="bg-purple-600 text-white w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                    1
                  </div>
                  <h5 className="elegant-subheading font-semibold mb-2 text-lg">Choose Your Photos</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">Select your favorite memories to print</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-600 text-white w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                    2
                  </div>
                  <h5 className="elegant-subheading font-semibold mb-2 text-lg">Customize Your Design</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">Add text, choose your style</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-600 text-white w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                    3
                  </div>
                  <h5 className="elegant-subheading font-semibold mb-2 text-lg">We Create Magic</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">High-res printing on premium chocolate</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-600 text-white w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                    4
                  </div>
                  <h5 className="elegant-subheading font-semibold mb-2 text-lg">Delivered Fresh</h5>
                  <p className="text-sm text-gray-600 leading-relaxed">Receive your edible masterpieces</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-12 text-white shadow-2xl">
          <h3 className="elegant-heading text-3xl sm:text-4xl font-bold mb-4 tracking-wide">
            {content.cta_section?.heading || "Let's Create Something Sweet and Memorable"}
          </h3>
          <p className="elegant-subheading text-xl sm:text-2xl mb-8 opacity-95">
            {content.cta_section?.description || 'Contact us today and we can transform your photos into unforgettable chocolate creations.'}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to={content.cta_section?.button1_link || '/edible-greetings'}>
              <Button className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-lg">
                {content.cta_section?.button1_text || 'Shop Now'}
              </Button>
            </Link>
            <Link to={content.cta_section?.button2_link || '/contact'}>
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-purple-600 px-8 py-6 text-lg font-semibold">
                {content.cta_section?.button2_text || 'Contact Us'}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="bg-gradient-to-b from-white to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="elegant-heading text-4xl sm:text-5xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="elegant-subheading text-xl sm:text-2xl text-gray-700">Real stories from people who love our chocolate creations</p>
          </div>

          {/* Client Video Testimonials */}
          <div className="mb-16">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {content.testimonial_videos.map((testimonial, index) => {
                const colors = ['purple', 'pink', 'blue', 'green'];
                const color = colors[index % colors.length];
                const initial = testimonial.customer_name.charAt(0);
                
                return (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                    <div className="rounded-xl overflow-hidden shadow-md mb-4 aspect-[9/16] max-w-sm mx-auto">
                      <video 
                        className="w-full h-full object-cover"
                        controls
                        poster="https://images.unsplash.com/photo-1511381939415-e44015466834?w=300&h=533&fit=crop&q=80"
                        preload="metadata"
                      >
                        <source src={testimonial.video_url} type="video/mp4" />
                      </video>
                    </div>
                    <div className="flex items-center mb-3">
                      <div className="flex text-yellow-400 text-lg">
                        {'★'.repeat(testimonial.rating || 5)}
                      </div>
                    </div>
                    <p className="text-gray-700 italic mb-4 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center">
                      <div className={`bg-${color}-100 w-10 h-10 rounded-full flex items-center justify-center mr-3`}>
                        <span className={`text-${color}-600 font-bold`}>{initial}</span>
                      </div>
                      <div>
                        <p className="elegant-subheading font-semibold text-gray-900">{testimonial.customer_name}</p>
                        <p className="text-gray-500 text-sm">{testimonial.customer_title}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Text Testimonials Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 hover:shadow-xl transition-shadow bg-white">
              <div className="flex text-yellow-400 mb-4">
                {'★'.repeat(5)}
              </div>
              <p className="text-gray-700 mb-4">
                "Ordered these for my mom's 60th birthday with family photos. She cried happy tears! 
                The quality is amazing and they taste delicious. Will definitely order again!"
              </p>
              <div className="flex items-center">
                <div className="bg-pink-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-pink-600 font-bold">J</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Jessica M.</p>
                  <p className="text-gray-500 text-sm">Birthday Gift</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-xl transition-shadow bg-white">
              <div className="flex text-yellow-400 mb-4">
                {'★'.repeat(5)}
              </div>
              <p className="text-gray-700 mb-4">
                "We used these for our corporate event with our company logo. The attention to detail 
                was perfect and our clients loved them. Professional service from start to finish!"
              </p>
              <div className="flex items-center">
                <div className="bg-blue-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">D</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">David R.</p>
                  <p className="text-gray-500 text-sm">Corporate Event</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-xl transition-shadow bg-white">
              <div className="flex text-yellow-400 mb-4">
                {'★'.repeat(5)}
              </div>
              <p className="text-gray-700 mb-4">
                "Best anniversary gift ever! My husband was so surprised to see our engagement photo 
                on chocolate. Such a creative and thoughtful idea. The chocolates are premium quality!"
              </p>
              <div className="flex items-center">
                <div className="bg-purple-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-bold">E</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Emily T.</p>
                  <p className="text-gray-500 text-sm">Anniversary</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-xl transition-shadow bg-white">
              <div className="flex text-yellow-400 mb-4">
                {'★'.repeat(5)}
              </div>
              <p className="text-gray-700 mb-4">
                "Ordered these as graduation party favors with my daughter's photo. Everyone at the party 
                was so impressed! Great quality chocolate and the photos looked perfect."
              </p>
              <div className="flex items-center">
                <div className="bg-green-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">M</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Maria L.</p>
                  <p className="text-gray-500 text-sm">Graduation Party</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-xl transition-shadow bg-white">
              <div className="flex text-yellow-400 mb-4">
                {'★'.repeat(5)}
              </div>
              <p className="text-gray-700 mb-4">
                "The personalized chocolates exceeded our expectations! We sent them as holiday gifts 
                to our clients. The response was overwhelmingly positive. Highly recommend!"
              </p>
              <div className="flex items-center">
                <div className="bg-amber-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-amber-600 font-bold">R</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Robert K.</p>
                  <p className="text-gray-500 text-sm">Corporate Gifts</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-xl transition-shadow bg-white">
              <div className="flex text-yellow-400 mb-4">
                {'★'.repeat(5)}
              </div>
              <p className="text-gray-700 mb-4">
                "I was skeptical at first, but wow! The photo clarity is incredible and the premium 
                chocolate is delicious. Made my boyfriend's birthday extra special. Thank you!"
              </p>
              <div className="flex items-center">
                <div className="bg-red-100 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-red-600 font-bold">L</span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Linda P.</p>
                  <p className="text-gray-500 text-sm">Birthday Surprise</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Stats Section */}
          <div className="mt-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-12 text-white">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-5xl font-bold mb-2">{content.stats?.happy_customers || '5000+'}</p>
                <p className="text-purple-100">Happy Customers</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2">{content.stats?.average_rating || '4.9★'}</p>
                <p className="text-purple-100">Average Rating</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2">{content.stats?.chocolates_created || '15000+'}</p>
                <p className="text-purple-100">Chocolates Created</p>
              </div>
              <div>
                <p className="text-5xl font-bold mb-2">{content.stats?.satisfaction || '100%'}</p>
                <p className="text-purple-100">Satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Founder Section */}
      <div className="py-20 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600" style={{ fontFamily: 'Playfair Display, serif' }}>
              Meet Our Founder
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 mx-auto mb-6"></div>
          </div>
          
          <Card className="overflow-hidden shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
            <div className="grid md:grid-cols-2 gap-8 p-8 md:p-12">
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="w-64 h-64 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-blue-500 p-1">
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                      <div className="w-60 h-60 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center text-6xl">
                        👨‍💼
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white px-6 py-2 rounded-full shadow-lg">
                    <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Akhil Gutta
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col justify-center space-y-6">
                <div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Founder & Visionary
                  </h3>
                  <p className="text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    With a passion for creating memorable experiences through innovative chocolate artistry, Akhil Gutta founded Portrait Chocolate to bring joy and sweetness to life's most special moments.
                  </p>
                </div>
                
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Get in Touch</h4>
                  
                  <div className="flex items-center space-x-3 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-white text-lg">📧</span>
                    </div>
                    <a href="mailto:potraitchocolate@gmail.com" className="text-gray-700 hover:text-purple-600 transition-colors font-medium">
                      potraitchocolate@gmail.com
                    </a>
                  </div>
                  
                  <div className="flex items-center space-x-3 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-white text-lg">📞</span>
                    </div>
                    <a href="tel:+18087562017" className="text-gray-700 hover:text-purple-600 transition-colors font-medium">
                      +1 808 756 2017
                    </a>
                  </div>
                  
                  <div className="flex items-center space-x-3 group">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-white text-lg">📍</span>
                    </div>
                    <span className="text-gray-700 font-medium">
                      Big Island, Hawaii, USA
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-500 italic mt-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                    "Every chocolate tells a story, and I'm here to help you tell yours."
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="elegant-heading font-bold text-xl mb-4">Portrait Chocolate</h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                Creating unique and personalized live photo printed chocolates for your special occasions.
              </p>
            </div>
            <div>
              <h4 className="elegant-heading font-bold text-xl mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/edible-greetings" className="block text-gray-300 hover:text-white text-sm transition-colors">
                  Edible Greetings
                </Link>
                <Link to="/contact" className="block text-gray-300 hover:text-white text-sm transition-colors">
                  Contact Us
                </Link>
                <Link to="/affiliate/register" className="block text-gray-300 hover:text-white text-sm transition-colors">
                  Become an Affiliate
                </Link>
                <Link to="/login" className="block text-gray-300 hover:text-white text-sm transition-colors">
                  Customer Login
                </Link>
              </div>
            </div>
            <div>
              <h4 className="elegant-heading font-bold text-xl mb-4">Contact Information</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <p>📧 <a href="mailto:potraitchocolate@gmail.com" className="hover:text-white transition-colors">potraitchocolate@gmail.com</a></p>
                <p>📞 <a href="tel:+18087562017" className="hover:text-white transition-colors">+1 808 756 2017</a></p>
                <p>📍 Big Island, Hawaii, USA</p>
                <p className="mt-4 text-xs italic">We're here to help! Reach out anytime.</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 Portrait Chocolate LLC. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PotraitChocolateLanding;
